import { useState, useCallback, useEffect, useRef } from 'react';
import type { AlertPayload, NotificationSettings } from '../types';

const STORAGE_KEY = 'system-status-notifications';

interface UseNotificationsReturn {
  settings: NotificationSettings;
  toggleEnabled: () => void;
  toggleSound: () => void;
  requestPermission: () => Promise<void>;
  handleAlert: (payload: AlertPayload) => void;
}

// Create alert sound using Web Audio API (no external file needed)
function playAlertSound(): void {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();

    // Create oscillator for beep sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Alert sound: two-tone beep (800Hz -> 600Hz -> 800Hz)
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.15);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.3);

    // Volume envelope
    gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);

    // Cleanup
    setTimeout(() => {
      audioContext.close();
    }, 600);
  } catch (err) {
    console.warn('[Notifications] Failed to play alert sound:', err);
  }
}

export function useNotifications(): UseNotificationsReturn {
  const [settings, setSettings] = useState<NotificationSettings>(() => {
    // Load from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          enabled: parsed.enabled ?? true,
          soundEnabled: parsed.soundEnabled ?? true,
          permission: Notification.permission,
        };
      } catch {
        // Ignore parse errors
      }
    }
    return {
      enabled: true,
      soundEnabled: true,
      permission: Notification.permission,
    };
  });

  const soundEnabledRef = useRef(settings.soundEnabled);

  // Keep ref updated
  useEffect(() => {
    soundEnabledRef.current = settings.soundEnabled;
  }, [settings.soundEnabled]);

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        enabled: settings.enabled,
        soundEnabled: settings.soundEnabled,
      }),
    );
  }, [settings.enabled, settings.soundEnabled]);

  // Update permission state when it changes
  useEffect(() => {
    const checkPermission = () => {
      setSettings((prev) => ({
        ...prev,
        permission: Notification.permission,
      }));
    };

    // Check initially
    checkPermission();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('[Notifications] Browser does not support notifications');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setSettings((prev) => ({
        ...prev,
        permission,
      }));
      console.log('[Notifications] Permission:', permission);
    } catch (err) {
      console.error('[Notifications] Failed to request permission:', err);
    }
  }, []);

  // Toggle notifications enabled
  const toggleEnabled = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  }, []);

  // Toggle sound enabled
  const toggleSound = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      soundEnabled: !prev.soundEnabled,
    }));
  }, []);

  // Play alert sound
  const playSound = useCallback(() => {
    if (!soundEnabledRef.current) return;
    playAlertSound();
  }, []);

  // Show browser notification
  const showNotification = useCallback(
    (title: string, body: string) => {
      if (!settings.enabled) return;
      if (settings.permission !== 'granted') return;

      try {
        new Notification(title, {
          body,
          icon: '/favicon.svg',
          tag: 'system-status-alert',
          requireInteraction: true,
        });
      } catch (err) {
        console.warn('[Notifications] Failed to show notification:', err);
      }
    },
    [settings.enabled, settings.permission],
  );

  // Handle alert from monitor
  const handleAlert = useCallback(
    (payload: AlertPayload) => {
      if (!settings.enabled) return;

      const serviceNames = payload.services.map((s) => s.name).join(', ');

      if (payload.type === 'down') {
        // Play sound for service down
        playSound();

        // Show notification
        const count = payload.services.length;
        showNotification(`🔴 ${count} Service${count > 1 ? 's' : ''} Down!`, serviceNames);
      } else if (payload.type === 'recovered') {
        // Show recovery notification (no sound)
        const count = payload.services.length;
        showNotification(`🟢 ${count} Service${count > 1 ? 's' : ''} Recovered`, serviceNames);
      }
    },
    [settings.enabled, playSound, showNotification],
  );

  return {
    settings,
    toggleEnabled,
    toggleSound,
    requestPermission,
    handleAlert,
  };
}
